import {
  getZohoRefreshToken,
  getCompany,
  getToken,
} from "../../../../shared/data/storage/authStorage";
import { createApiError } from "../../../../utils/errorHandling";
import {
  CustomerDataDTO,
  CustomerResponseDTO,
  CustomerSearchResponseDTO,
  AllCustomersResponseDTO,
  CustomerInvoicesResponseDTO,
  CustomerDetailsResponseDTO,
} from "../models/CustomerDTOs";
import { API_BASE_URL } from "../../../../config/api";

export const createCustomer = async (
  customerData: CustomerDataDTO
): Promise<CustomerResponseDTO> => {
  const token = getToken();
  const companyData = getCompany();
  customerData.refresh_token = getZohoRefreshToken() || undefined;

  const response = await fetch(`${API_BASE_URL}/api/invoices/customers`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      customerData: customerData,
      company_gst_enabled: companyData?.companyGstEnabled,
    }),
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to create customer",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const updateCustomer = async (
  customerId: string,
  customerData: Partial<CustomerDataDTO>
): Promise<CustomerResponseDTO> => {
  const token = getToken();
  const companyData = getCompany();
  customerData.refresh_token = getZohoRefreshToken() || undefined;

  const response = await fetch(
    `${API_BASE_URL}/api/invoices/customers/${customerId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        customerData: customerData,
        company_gst_enabled: companyData?.companyGstEnabled,
      }),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to update customer",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const searchCustomers = async (
  params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
  } = {}
): Promise<{
  success: boolean;
  data: CustomerDataDTO[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
}> => {
  const token = getToken();

  const queryParams = new URLSearchParams();
  if (params.page) queryParams.append("page", params.page.toString());
  if (params.limit) queryParams.append("limit", params.limit.toString());
  if (params.search) queryParams.append("search", params.search);
  if (params.status) queryParams.append("status", params.status);

  const url = `${API_BASE_URL}/api/customers${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to search customers",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const fetchCustomers = async (
  query: string,
  options?: {
    customerId?: string;
    page?: number;
    limit?: number;
  }
): Promise<CustomerSearchResponseDTO> => {
  const token = getToken();

  const queryParams = new URLSearchParams();
  const trimmedQuery = query.trim();
  const trimmedCustomerId = options?.customerId?.trim();

  if (trimmedQuery) queryParams.append("q", trimmedQuery);
  if (trimmedCustomerId) queryParams.append("customer_id", trimmedCustomerId);
  if (options?.page) queryParams.append("page", options.page.toString());
  if (options?.limit) queryParams.append("limit", options.limit.toString());

  const url = `${API_BASE_URL}/api/invoices/customers/search${
    queryParams.toString() ? `?${queryParams.toString()}` : ""
  }`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to search customers",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const getCustomerById = async (
  customerId: string
): Promise<CustomerDetailsResponseDTO> => {
  const token = getToken();

  const response = await fetch(
    `${API_BASE_URL}/api/invoices/customers/${customerId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch customer details",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const getAllCustomers = async (): Promise<AllCustomersResponseDTO> => {
  const token = getToken();

  const response = await fetch(`${API_BASE_URL}/api/invoices/customers`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch all customers",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const getInvoicesForCustomer = async (
  customerZohoId: string
): Promise<CustomerInvoicesResponseDTO> => {
  const token = getToken();

  const url = `${API_BASE_URL}/api/invoices/getInvoiceForCustomer?customer_id=${encodeURIComponent(
    customerZohoId
  )}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch customer invoices",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const getOutstandingInvoicesForCustomer = async (
  customerZohoId: string,
  limit: number = 20,
  offset: number = 0,
  search?: string
): Promise<CustomerInvoicesResponseDTO> => {
  const token = getToken();

  const params = new URLSearchParams({
    customer_id: customerZohoId,
    limit: limit.toString(),
    offset: offset.toString(),
  });

  if (search) {
    params.append("search", search);
  }

  const url = `${API_BASE_URL}/api/invoices/getOutstandingInvoicesForCustomer?${params.toString()}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to fetch outstanding customer invoices",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const categorizeTransaction = async (requestBody: {
  transaction_id: string;
  categorize_in_zoho_json: {
    customer_id: string;
    invoices: Array<{
      invoice_id: string;
      amount_applied: number;
    }>;
    amount: number;
    account_id: string;
  };
}): Promise<{
  success: boolean;
  message: string;
  data?: any;
}> => {
  const token = getToken();

  const response = await fetch(
    `${API_BASE_URL}/api/bank-transactions/categorize`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    }
  );

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Failed to parse error response" };
    }

    throw createApiError(
      errorData.message || "Failed to categorize transaction",
      response.status,
      response.statusText
    );
  }

  return response.json();
};

export const customersAPI = {
  createCustomer,
  updateCustomer,
  searchCustomers,
  fetchCustomers,
  getCustomerById,
  getAllCustomers,
  getInvoicesForCustomer,
  categorizeTransaction,
};

// Re-export types
export type {
  CustomerData,
  CustomerResponse,
  CustomerSearchResponse,
  CustomerSearchItem,
  AllCustomersResponse,
  CustomerListItem,
  CustomerInvoice,
  CustomerInvoicesResponse,
} from "../models/types";
